// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

// api types
import type { TimeSeriesDataPoint } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import { PortfolioKpiSparkline } from './PortfolioKpiSparkline';

interface DeepDiveCountKpiCardProps {
  title: string;
  subtitle: string;
  value: number | null;
  color: string;
  icon: React.ReactElement;
  unit?: string;
  isLoading?: boolean;
  secondaryLabel?: string;
  secondaryValue?: number | null;
  secondaryUnit?: string;
  sparklineData?: TimeSeriesDataPoint[];
}

const DeepDiveCountKpiCard = ({
  title,
  subtitle,
  value,
  color,
  icon,
  unit,
  isLoading = false,
  secondaryLabel,
  secondaryValue,
  secondaryUnit,
  sparklineData,
}: DeepDiveCountKpiCardProps) => {
  const { t } = useTranslation('energy-analysis');

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ height: '100%', p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5} height="100%">
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={1.5}
          >
            <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
              <Avatar
                variant="rounded"
                sx={{
                  bgcolor: alpha(color, 0.1),
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                }}
              >
                {React.cloneElement(icon, {
                  sx: {
                    verticalAlign: 'middle',
                    color,
                    fontSize: 20,
                  },
                })}
              </Avatar>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {title}
              </Typography>
            </Box>
          </Box>

          {isLoading ? (
            <Box display="flex" alignItems="center" minHeight={42}>
              <CircularProgress size={20} />
            </Box>
          ) : value == null ? (
            <Typography variant="caption" color="text.secondary">
              {t('portfolio.kpi_cards.no_data')}
            </Typography>
          ) : (
            <Box>
              <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                <Typography variant="h5" fontWeight={700} lineHeight={1.1}>
                  {formatNumber(value)}
                </Typography>
                {unit ? (
                  <Typography variant="body2" color="text.secondary">
                    {unit}
                  </Typography>
                ) : null}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
              {secondaryLabel && secondaryValue != null ? (
                <Box
                  display="flex"
                  alignItems="baseline"
                  gap={0.75}
                  flexWrap="wrap"
                  mt={0.5}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {formatNumber(secondaryValue)}
                  </Typography>
                  {secondaryUnit ? (
                    <Typography variant="caption" color="text.secondary">
                      {secondaryUnit}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    {secondaryLabel}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          )}
          {!isLoading && sparklineData?.length ? (
            <Box mt="auto !important">
              <PortfolioKpiSparkline
                dataPoints={sparklineData}
                unit={unit}
                color={color}
              />
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DeepDiveCountKpiCard;
